export type DeepPartial<T> = T extends object
    ? {
          [P in keyof T]?: DeepPartial<T[P]>;
      }
    : T;

export interface Approval {
    id: string;
    active: boolean;
    contract_start_date: string;
    term_length_in_days: number;
    qualified_amount: number;
    max_days_outstanding: number;
    origination_fee_percentage: number;
    advance_rate: number;
    funds_available: number;
    funds_in_use: number;
    number_of_active_deals: number;
    total_payoff_amount: number;
    total_in_process_amount: number;
    total_early_payoff_amount: number;
    total_savings_from_payoff: number;
    company: string;
    company_name: string;
    product_name: ProductName | string;
    payment_schema?: 'BULLET' | 'WEEKLY' | string | null;
    offer_schema?: Array<{
        payment_frequency: string;
        number_of_payments: number;
        first_payment_delay: number;
    }>;
}

export interface RateCurve {
    id: string;
    created_date: string;
    updated_date: string;
    day: number;
    daily_rate: number;
    approval: string;
}

export interface Application {
    id: string;
    first_name: string;
    last_name: string;
    home_address: string;
    email: string;
    phone_number: string;
    company_legal_name: string;
    business_ein: string;
    date_of_incorporation: string;
    ownership_percent: string;
    monthly_debt_pmt: string;
    monthly_revenue: string;
    company_address: string;
    website: string;
    ssn: string;
    personal_credit_score: string;
    date_of_birth: string;
    additional_owners: string;
    business_licenses: string;
    meta: {
        [key: string]: any;
    };
}

export interface Company {
    id: string;
    partner_company_uuid: string | null;
    partner_company_id?: string | null;
    status: CompanyStatus;
    company_name: string;
    company_address: string;
    partner_name: string | null;
    fundnow_status: ProductStatus;
    paylater_status: ProductStatus;
    drawdown_status: ProductStatus;
    first_name: string | null;
    last_name: string | null;
    phone_number: string | null;
    email: string | null;
    bank_name: string | null;
    bank_routing?: string | null;
    bank_account?: string | null;
    data_consent?: boolean;
}

export interface Deal {
    id: string;
    invoice_number: string;
    partner_invoice_uuid: string | null;
    filename: string | null;
    funded_date: string;
    first_payment_date: string;
    payment_frequency: string;
    number_of_payments: number;
    gross_funded_amount: number;
    origination_fee_amount: number;
    advance_amount: number;
    expected_rate: number;
    disbursement_to_company: number;
    disbursement_to_vendor: number;
    disbursement_to_customer: number;
    payback_amount: number;
    total_remaining: number;
    total_collected: number;
    periodic_collection_from_company: number;
    total_principal_collected: number;
    total_principal_remaining: number;
    collection_from_vendor: number;
    collection_from_company: number;
    collection_from_customer: number;
    status: DealDisplayStatus;
    deal_status: DealStatus;
    company: string;
    vendor: string | null;
    vendor_name: string;
    customer: string | null;
    customer_name: string;
    approval: string;
    company_name: string;
    product_name: ProductName | string;
    selected_offer: string | null;
    return_url: string | null;
    web_url: string | null;
}

interface InvoiceBase {
    id: string;
    status: InvoiceStatus;
    company: string;
    invoice_number: string;
    date: string;
    due_date: string;
    order_date: string | null;
    subtotal: number;
    tax: number;
    total: number;
    filename: string | null;
    payment_terms: string;
    ship_to_address: string;
    shipping_details: {
        shipping_zip: string | null;
        shipping_city: string | null;
        shipping_phone: string | null;
        shipping_address1: string | null;
        shipping_address2: string | null;
        shipping_province: string | null;
        shipping_last_name: string | null;
        shipping_first_name: string | null;
        shipping_province_code: string | null;
    };
    archived: boolean;
    web_url?: string | null;
}

export interface Bill extends InvoiceBase {
    vendor: string;
    vendor_name: string;
    is_checkout: boolean;
    return_url: string | null;
}

export interface Invoice extends InvoiceBase {
    customer: string;
    customer_name: string;
    partner_invoice_uuid: string | null;
    delivery_date: string;
}

export interface Offer {
    id: string;
    total_price: number;
    advance_amount: number;
    origination_fee: number;
    weekly_payment: number;
    rate: number;
    interest: number;
    number_of_payments: number;
    total_payments: number;
    merchant_fee: number;
    company: string;
    bill: string | null;
    invoice: string | null;
}

export interface Vendor {
    id?: string;
    bank_account?: string;
    bank_name?: string;
    bank_routing?: string;
    company?: string;
    company_address?: string;
    company_name?: string;
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    email?: string;
    partner_company_uuid?: string;
    partner_company_id?: string;
    partner_name?: string;
    status?: number;
    fundnow_status?: boolean;
    paylater_status?: boolean;
    drawdown_status?: boolean;
}

export interface Customer {
    id?: string;
    bank_account?: string;
    bank_name?: string;
    bank_routing?: string;
    company?: string;
    company_address?: string;
    company_name?: string;
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    email?: string;
    partner_company_uuid?: string;
    partner_company_id?: string;
    partner_name?: string;
    status?: number;
    fundnow_status?: boolean;
    paylater_status?: boolean;
    drawdown_status?: boolean;
}

export type PaymentMethod = 'ACHQ' | 'Wire';
export type PaymentDirection = 'From Customer' | 'To Customer';
export type PaymentEventName =
    | 'Submitted'
    | 'Cleared'
    | 'Returned-NSF'
    | 'Cancelled'
    | 'Returned-Other';
export type PaymentStatus =
    | 'Scheduled'
    | 'In-Progress'
    | 'Cleared'
    | 'Returned-NSF'
    | 'Cancelled'
    | 'Returned-Other'
    | 'Rejected'
    | 'Charged Back';

// export const DESCRIPTION_CODE_CHOICES: Record<string, string> = {
//     '001': 'Vendor Disburse',
//     '002': 'Company Disburse',
//     '003': 'Customer Disburse',
//     '004': 'Vendor Collection',
//     '005': 'Company Collection',
//     '006': 'Customer Collection',
//     '111': 'Origination Fee to Servicer',
//     '112': 'Transaction Fee to Servicer',
//     '113': 'Servicing Fee to Servicer',
//     '201': 'Investor Deposit',
//     '202': 'Investor Withdraw',
//     '301': 'Partner Collection',
//     '302': 'Partner Payout',
//     '303': 'Partner Sales',
//     '304': 'Partner Batch',
// };

// export type DescriptionCode = keyof typeof DESCRIPTION_CODE_CHOICES;

export enum DescriptionCode {
    VendorDisburse = '001',
    CompanyDisburse = '002',
    CustomerDisburse = '003',
    VendorCollection = '004',
    CompanyCollection = '005',
    CustomerCollection = '006',
    OriginationFeeToServicer = '111',
    TransactionFeeToServicer = '112',
    ServicingFeeToServicer = '113',
    InvestorDeposit = '201',
    InvestorWithdraw = '202',
    PartnerCollection = '301',
    PartnerPayout = '302',
    PartnerSales = '303',
    PartnerBatch = '304',
}

export const DESCRIPTION_CODE_LABEL: Record<DescriptionCode, string> = {
    [DescriptionCode.VendorDisburse]: 'Vendor Disburse',
    [DescriptionCode.CompanyDisburse]: 'Company Disburse',
    [DescriptionCode.CustomerDisburse]: 'Customer Disburse',
    [DescriptionCode.VendorCollection]: 'Vendor Collection',
    [DescriptionCode.CompanyCollection]: 'Company Collection',
    [DescriptionCode.CustomerCollection]: 'Customer Collection',
    [DescriptionCode.OriginationFeeToServicer]: 'Origination Fee to Servicer',
    [DescriptionCode.TransactionFeeToServicer]: 'Transaction Fee to Servicer',
    [DescriptionCode.ServicingFeeToServicer]: 'Servicing Fee to Servicer',
    [DescriptionCode.InvestorDeposit]: 'Investor Deposit',
    [DescriptionCode.InvestorWithdraw]: 'Investor Withdraw',
    [DescriptionCode.PartnerCollection]: 'Partner Collection',
    [DescriptionCode.PartnerPayout]: 'Partner Payout',
    [DescriptionCode.PartnerSales]: 'Partner Sales',
    [DescriptionCode.PartnerBatch]: 'Partner Batch',
} as const;

export interface Payment {
    id: string;
    guts_external_uuid: string | null;
    created_date: string;
    update_date: string;
    payment_method: PaymentMethod;
    reference_id: string;
    confirmation_number: string;
    guts_reference_id: string | null;
    description: string;
    date_created: string;
    date_scheduled: string | null;
    check_amount: number;
    payment_direction: PaymentDirection;
    event_name: PaymentEventName | null;
    event_date: string;
    event_time: string | null;
    resulting_status: PaymentStatus;
    return_codes: string;
    description_code: DescriptionCode;
    notes: string | null;
    deal: string;
    company: string | null;
    customer_name: string;
    product_name: ProductName | string | null;
    paykey?: string | null; // Straddle paykey
    straddle_payment_id?: string | null; // Straddle payment id
}

export enum ApplicationStatus {
    InProgress,
    Submitted,
    Approved,
    Rejected,
}

export enum CompanyStatus {
    Lead,
    Pending,
    Approved,
    Rejected,
}

export enum InvoiceStatus {
    Created,
    Initialized,
    Archived,
}

// export enum DealStatus {
//     Available,
//     Submitted,
//     InProgress,
//     Overdue,
//     Complete,
//     Invalid,
// }

export enum DealStatus {
    'Submitted' = 'Submitted',
    'Completed' = 'Completed',
    'Early Completed' = 'Early Payoff',
    'Current' = 'Current',
    'Grace Period' = 'Grace Period',
    'Late' = 'Late',
    'Default' = 'Default',
}

export enum DealDisplayStatus {
    /**
     * Legacy status values returned as Deal.status from backend (may be inaccurate).
     * Prefer DealStatus (Deal.deal_status) when available - it's more reliable.
     *
     * @deprecated Use DealStatus instead when Deal.deal_status is available
     * @see DealStatus for the preferred enum
     */

    NoStatus, // 0, don't exist in backend
    Submitted, // 1, submitted
    Current, // 2, days_past_due <= 15
    GracePeriod, // 3, days_past_due >=16
    PaidOff, // 4, payoff_check(date)
    Rejected, // 5, rejected_by_lendica = true
}

export interface SaaSDisplayData {
    name?: string; // account name
    account_number?: string; // masked account number from lens
    institution?: string; // SaaS name
}

export interface DataConnection {
    id: string;
    active: boolean;
    company: string;
    company_name: string;
    display_data: SaaSDisplayData[] | any;
    saas_category: string;
    saas_name: string;
}

export enum InvoiceTableRowStatus {
    Ready = 'Ready', // invoice.status = 0
    Initialized = 'Initialized', // invoice.status = 1
    Archived = 'Archived', // invoice.status = 2
    UnderReview = 'Pending approval', // deal.deal_status = "Submitted"
    Active = 'Active', // deal.deal_status = "Current"
    GracePeriod = 'Grace period', // deal.deal_status = "Grace Period"
    Late = 'Late', // deal.deal_status = "Late"
    PaidOff = 'Paid off', // deal.deal_status = "Early Completed" || deal.deal_status = "Completed"
    Defaulted = 'Defaulted', // deal.deal_status = "Default"
    Rejected = 'Not approved', // deal.status = 5
}

export enum ProductStatus {
    NotActivated, // 0, no approval
    Pending, // 1, active but not signed
    ActiveAndSigned, // 2, signed and active
    Paused, // 3, signed but not active
}

export enum ProductName {
    PayLater = 'paylater',
    FundNow = 'fundnow',
    DrawDown = 'drawdown',
}

export type ProductNameLabel = 'PayLater' | 'FundNow' | 'DrawDown';

export type EventData = {
    eventName: string;
    displayMessage: string;
};

/* Below are Bill and Invoice Payload types for the iBranch partner submit bill and invoice endpoints */
export interface InvoicePayload {
    partner_invoice_uuid: string; // required
    total: number; // required
    order_date: string; // required, format: yyyy-mm-dd
    due_date: string; // required, format: yyyy-mm-dd
    invoice_number?: string;
    last_updated_datetime?: string; // format: yyyy-mm-ddT00:00:00Z
    status?: string;
    payment_terms?: string;
    subtotal: number; // required
    tax: number; // required
    shipping_total?: number;
    settled: boolean; // required
    company: PayloadCompany;
    customer: InvoicePayloadCustomer;
    line_items?: PayloadLineItem[];
    child_invoices?: InvoicePayload[];
}

export interface BillPayload {
    partner_invoice_uuid: string; // required
    total: number; // required
    order_date: string; // required, format: yyyy-mm-dd
    due_date: string; // required, format: yyyy-mm-dd
    invoice_number?: string;
    last_updated_datetime?: string; // format: yyyy-mm-ddT00:00:00Z
    status?: string;
    payment_terms?: string;
    subtotal: number; // required
    tax: number; // required
    shipping_total?: number;
    settled: boolean; // required
    company: PayloadCompany;
    vendor: BillPayloadVendor;
    line_items?: PayloadLineItem[];
    child_bills?: BillPayload[];
}

export interface PayloadCompany {
    partner_company_uuid: string; // required
    company_name: string; // required
    first_name?: string;
    last_name?: string;
    phone_number?: string;
    email?: string;
    company_address?: string;
    ein_number?: string;
}

export interface InvoicePayloadCustomer {
    partner_company_uuid: string; // required
    company_name: string; // required
    bank_name?: string;
    bank_routing?: string;
    bank_account?: string;
    email?: string;
    phone_number?: string;
    company_address?: string;
}

export interface BillPayloadVendor {
    partner_company_uuid: string; // required
    company_name: string; // required
    bank_name: string; // required
    bank_routing: string; // required
    bank_account: string; // required
    email?: string;
    phone_number?: string;
    company_address?: string;
}

export interface PayloadLineItem {
    product_id?: string;
    product_name?: ProductName | string;
    quantity?: number;
    unit_price?: number;
    unit_of_measure?: string;
    line_total?: number;
}
/* End of Bill and Invoice Payload types */

/* V2 API Types */

export interface User {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
}

export enum UserType {
    Owner = 1,
    Employee = 2,
    Admin = 3,
}

export type UserTypeLabel = 'Owner' | 'Employee' | 'Admin';

export interface Member {
    id: string;
    user: User;
    user_type: UserType;
    user_type_display: UserTypeLabel;
    is_active_in_company: boolean;
    add_time: string;
    update_time: string;
}

export interface Membership {
    id: string;
    company: string; // company_id
    user: User;
    user_type: UserType;
    is_active_in_company: boolean;
    add_time: string;
    update_time: string;
}

export type InvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';

export interface Invitation {
    id: string;
    email: string;
    role_to_grant: UserType;
    role_to_grant_display: UserTypeLabel;
    status: InvitationStatus | string;
    created_at: string;
    expires_at: string;
    invited_by: User;
}

export type OfferTermsInvitationStatus =
    | 'NEW'
    | 'SENT'
    | 'STARTED'
    | 'SUBMITTED'
    | 'APPROVED'
    | 'REJECTED';

export enum OfferTermsInvitationRowStatus {
    NewDraft = 'New Draft', // status = 'NEW'
    InvitationSent = 'Invitation Sent', // status = 'SENT'
    ApplicationStarted = 'Application Started', // status = 'STARTED'
    ApplicationSubmitted = 'Application Submitted', // status = 'SUBMITTED'
    CustomerApproved = 'Customer Approved', // status = 'APPROVED'
    CustomerRejected = 'Customer Rejected', // status = 'REJECTED'
}

export interface OfferTermsInvitation {
    id: string;
    company_name: string;
    email: string;
    seller_company_name: string;
    product_name: ProductName | string;
    status: OfferTermsInvitationStatus | string;
    status_display: string;
    expires_at: string;
    is_archived: boolean;
    email_content: string;
    created_at: string;
    updated_at: string;
}

export interface OfferTermsInvitationActivityLog {
    id: string;
    action: string;
    action_display: string;
    actor_email: string | null;
    timestamp: string;
    details: Record<string, any>;
}

export interface OfferTermsInvitationDetail extends OfferTermsInvitation {
    company?: string; // company id
    product?: string; // product id
    invited_by?: string;
    invited_by_email?: string;
    accepted_by?: string | null;
    activity_logs?: OfferTermsInvitationActivityLog[];
}

// Shared API error payload type used by normalizers/clients
export type ApiErrorPayload =
    | string
    | {
          message?: string;
          error?: string;
          detail?: string;
          errors?: string[] | { message?: string }[] | Record<string, string | string[]>;
      };
